/* eslint-disable camelcase */
import { Form, Formik } from 'formik';
import _, { isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import FormSelect from '../../../components/dropdown/formSelect';
import InputText from '../../../components/input-text/inputText';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import CustomTextArea from '../../../components/text-area/textArea';
import { DATE_FORMAT, DATE_FORMAT_WITH_TIME } from '../../../constants/common';
import { useTradingBlackoutEligibilityOptions, useWealthlaneCustomerApi } from '../../../hooks';
import usePostTradingBlackoutConfig from '../../../hooks/post/usePostTradingBlackoutConfig';
import {
  type Wealthlane_CustomerService_Dtos_Blackout_CreateUpdateBlackOutGroupDto,
  type Wealthlane_CustomerService_Dtos_Blackout_CreateUpdateBlackoutTitleDto,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { type ModalStatusType, type OptionType, type UserType } from '../../../types/common';
import { TRADING_BLACKOUT_CONFIG_VALIDATION } from '../../../utils/validations/validations';
import { TradePreClearanceContainer } from '../trade-pre-clearance/tradePreClearance.styled';
import { GroupOptions } from './trading-blackout-options';
import MaterialTimePicker from '../../../components/material-time-picker/materialTimePicker';
import { RadioButtonWrapper } from '../../../components/radio-button/radioButton.styled';
import { LabelNormal } from '../../../components/input-text/input.styled';
import 'moment-timezone';

interface TradingBlackoutConfigurationType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  initialBlackoutConfig: any;
  departmentOptions?: OptionType[];
  employeeTypeOptions?: OptionType[];
  jobTitleOptions?: UserType[];
  loading: boolean;
  id?: string;
}

const TradingBlackoutConfigurationModel = ({
  isModalOpen,
  closeModal,
  modalStatus,
  setModalStatus,
  initialBlackoutConfig,
  loading,
  departmentOptions,
  employeeTypeOptions,
  jobTitleOptions,
  id,
}: TradingBlackoutConfigurationType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: blackoutEligibilityOptions, isLoading: isBlackoutEligibilityLoading } =
    useTradingBlackoutEligibilityOptions(handleRequest);

  const { mutate, isLoading } = usePostTradingBlackoutConfig(handleRequest, closeModal);

  // Get current time in EST timezone
  const getCurrentESTTime = (): string => {
    return moment.tz('America/New_York').format('HH:mm');
  };

  const getTodayESTDate = (): Date => {
    const estMoment = moment.tz('America/New_York');
    // Get EST date components and create a Date object with those values
    // This ensures the date part matches EST, not local timezone
    const year = estMoment.year();
    const month = estMoment.month(); // 0-indexed (0-11)
    const day = estMoment.date();
    // Create Date object with EST date values (time will be at midnight local time)
    return new Date(year, month, day);
  };

  // Combine date and time into a datetime string with timezone offset
  // Takes EST date/time from user input and sends as EST to API (no UTC conversion)
  const combineDateTime = (
    date: Date | null,
    time: string | null,
    isStartImmediate: boolean = false
  ): string | null => {
    if (!date) return null;

    const timeToUse = isStartImmediate ? getCurrentESTTime() : time;
    if (!timeToUse) return null;

    const dateStr = moment(date).format('YYYY-MM-DD');
    const dateTimeStr = `${dateStr}T${timeToUse}:00`;

    // Convert to EST timezone and format with timezone offset
    const estDateTime = moment.tz(dateTimeStr, 'America/New_York');
    // Format: YYYY-MM-DDTHH:mm:ss-05:00 (or -04:00 during DST)
    return estDateTime.format('YYYY-MM-DDTHH:mm:ssZ').replace(/([+-]\d{2})(\d{2})/, '$1:$2');
  };


  const handleSubmit = (values: any): void => {
    console.log('=== FORM SUBMIT TRIGGERED ===');
    console.log('Raw Form Values:', values);

    // Combine EST date/time from user input and send as EST to API (no UTC conversion)
    const startDateTime = combineDateTime(
      values.startDate,
      values.startTime,
      values.startImmediately
    );

    const endDateTime = combineDateTime(
      values.endDate,
      values.endTime,
      false  // endImmediately removed - always use selected time
    );

    console.log('Combined DateTimes (EST for API):', {
      startDateTime,
      endDateTime
    });

    const payloadValues = { ...values };
    delete payloadValues.startImmediately;
    delete payloadValues.endImmediately;
    delete payloadValues.startTime;
    delete payloadValues.endTime;
    delete payloadValues.originalEndDate;
    delete payloadValues.originalStartDate;



    const payload = {
      ...payloadValues,
      id: Boolean(values.id) && modalStatus === 'Edit' ? values?.id : null,
      // Replace separate date/time fields with combined datetime
      startDate: startDateTime,
      endDate: endDateTime,
      // Remove UI-only fields
      // startTime: undefined,
      // endTime: undefined,
      // startImmediately: undefined,
      // Add isImmediateStart to API payload
      // isImmediateStart: values.startImmediately,
      // Keep lastModifiedDate - API may need it for edit operations
      blackoutGroups: !isEmpty(values?.blackoutGroups)
        ? values?.blackoutGroups?.map(
          (item: Wealthlane_CustomerService_Dtos_Blackout_CreateUpdateBlackOutGroupDto) => ({
            ...item,
            blackOutJobTitles: item.blackOutJobTitles?.map(
              (
                jobTitleId: Wealthlane_CustomerService_Dtos_Blackout_CreateUpdateBlackoutTitleDto
              ) => ({
                customerTitleId: jobTitleId,
              })
            ),
          })
        )
        : null,
    };

    console.log('=== FINAL API PAYLOAD ===');
    console.log('Payload:', payload);
    console.log('Original Form Values:', {
      startDate: values.startDate,
      startTime: values.startTime,
      startImmediately: values.startImmediately,
      endDate: values.endDate,
      endTime: values.endTime,
    });

    console.log('=== CALLING API (mutate) ===');
    mutate(payload);
  };



  return (
    <Formik
      enableReinitialize
      initialValues={initialBlackoutConfig}
      validationSchema={TRADING_BLACKOUT_CONFIG_VALIDATION}
      onSubmit={handleSubmit}
      validate={(values) => {
        console.log('=== FORMIK VALIDATE RUNNING ===');
        console.log('Validation Errors Check:', {
          hasStartDate: !!values.startDate,
          hasStartTime: !!values.startTime,
          startImmediately: values.startImmediately,
          hasEndDate: !!values.endDate,
          hasEndTime: !!values.endTime,
        });

        const errors: any = {};

        // Validate start datetime is in the present or future (EST) - skip when "Start Immediately" is checked
        if (values.startDate && values.startTime && !values.startImmediately) {
          const currentEST = moment.tz('America/New_York');
          const selectedDateTime = moment.tz(
            `${moment(values.startDate).format('YYYY-MM-DD')}T${values.startTime}:00`,
            'America/New_York'
          );

          // // Check if start datetime is in the past
          // if (selectedDateTime.isBefore(currentEST)) {
          //   errors.startTime = 'Start date and time must be in the present or future';
          // }
        }

        // Validate end datetime is after start datetime
        if (values.startDate && values.startTime && values.endDate && values.endTime) {
          const startDateTime = moment.tz(
            `${moment(values.startDate).format('YYYY-MM-DD')}T${values.startTime}:00`,
            'America/New_York'
          );
          const endDateTime = moment.tz(
            `${moment(values.endDate).format('YYYY-MM-DD')}T${values.endTime}:00`,
            'America/New_York'
          );

          if (endDateTime.isSameOrBefore(startDateTime)) {
            errors.endTime = 'End time must be after start time';
          }
        }

        return errors;
      }}
    >
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values, submitForm }) => {
        console.log('=== FORMIK RENDER ===');
        console.log('Current Errors:', errors);
        console.log('Current Values:', {
          startDate: values.startDate,
          startTime: values.startTime,
          startImmediately: values.startImmediately,
          endDate: values.endDate,
          endTime: values.endTime,
        });
        const getApiStartDateTime = (): moment.Moment | null => {
          if (initialBlackoutConfig?.originalStartDate) {
            return moment(initialBlackoutConfig.originalStartDate).tz('America/New_York');
          }

          if (initialBlackoutConfig?.startDate && initialBlackoutConfig?.startTime) {
            return moment.tz(
              `${moment(initialBlackoutConfig.startDate).format('YYYY-MM-DD')}T${initialBlackoutConfig.startTime}:00`,
              'America/New_York'
            );
          }

          return null;
        };

        const isStartDateTimeExpired =
          modalStatus === 'Edit' &&
          (() => {
            const apiStartDateTime = getApiStartDateTime();
            if (!apiStartDateTime) return false;

            const currentEST = moment.tz('America/New_York');
            return apiStartDateTime.isSameOrBefore(currentEST);
          })();

        // Auto-update time when "Start Immediately" is checked
        useEffect(() => {
          let startInterval: NodeJS.Timeout | null = null;

          if (values.startImmediately) {
            // Update start time immediately
            setFieldValue('startTime', getCurrentESTTime());
            setFieldValue('startDate', getTodayESTDate());

            // Update every minute
            startInterval = setInterval(() => {
              setFieldValue('startTime', getCurrentESTTime());
              setFieldValue('startDate', getTodayESTDate());
            }, 60000); // 60000ms = 1 minute
          }

          // Cleanup interval when checkbox is unchecked or component unmounts
          return () => {
            if (startInterval) clearInterval(startInterval);
          };
        }, [values.startImmediately]);

        // Clear end time whenever end date is cleared
        useEffect(() => {
          if (!values.endDate && values.endTime) {
            setFieldValue('endTime', null);
          }
        }, [values.endDate, values.endTime, setFieldValue]);



        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            size={'lg'}
            valueEdited={_.isEqual(values, initialBlackoutConfig)}
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus
              } Trading Blackout Configuration`}
            id={id}
            children={
              <Form id="tradingBlackoutConfigForm">
                <ModalBodyComponent id="tradingBlackoutConfigModalBody">
                  <div className='flex flex-col justify-between' id="tradingBlackoutConfigFormContainer">
                    <div id="tradingBlackoutConfigFormFields">
                      <TradePreClearanceContainer className='grid grid-cols-12 gap-x-5'>
                        {/* Last Modified Timestamp */}
                        {(modalStatus === 'Edit' || modalStatus === 'View') && values.lastModifiedDate && (
                          <div className='col-span-12 mb-4 p-3 bg-gray-50 rounded-md border border-gray-200' id="lastModifiedContainer">
                            <div className='flex items-center gap-2'>
                              <span className='material-symbols-outlined text-gray-500' style={{ fontSize: '18px' }}>
                                schedule
                              </span>
                              <span className='text-sm text-gray-600'>
                                <strong>Last Modified:</strong> {moment(values.lastModifiedDate).format(DATE_FORMAT_WITH_TIME)} EST
                              </span>
                            </div>
                          </div>
                        )}

                        <InputText
                          className='col-span-12'
                          type='text'
                          label='Blackout Title'
                          name='blackoutTitle'
                          id='blackoutTitle'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          disabled={modalStatus === 'View'}
                          required
                        />
                        {/* <InputText
                          className='col-span-12'
                          type='text'
                          label='Message'
                          name='message'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          disabled={modalStatus === 'View'}
                          required
                        /> */}
                        <CustomTextArea
                          containerClassName='col-span-12'
                          label='Message'
                          name='message'
                          id='message'
                          onChange={handleChange}
                          errors={errors}
                          inputType={modalStatus}
                          disabled={modalStatus === 'View'}
                          touched={touched}
                          required
                        />
                        {/* START DATE AND TIME - Same row */}

                        <div className='col-span-6' id="startDateContainer">
                          <CustomDatePicker
                            label='Start Date'
                            name='startDate'
                            id='startDate'
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            values={values}
                            min={getTodayESTDate()}
                            onChange={() => {
                              setFieldValue('endDate', null);
                            }}
                            disabled={modalStatus === 'View' ? true : isStartDateTimeExpired}
                            required
                          />
                        </div>

                        <div className='col-span-6' id="startTimeContainer">
                          <MaterialTimePicker
                            label='Start Time (EST)'
                            name='startTime'
                            id='startTime'
                            value={values.startTime}
                            onChange={(name, value) => {
                              setFieldValue(name, value);
                              if (modalStatus === 'Add' || modalStatus === 'Edit') {
                                setFieldValue('startImmediately', false);
                              }

                              if (
                                value &&
                                values.endTime &&
                                values.startDate &&
                                values.endDate &&
                                moment(values.startDate).isSame(values.endDate, 'day')
                              ) {
                                const newStartTime = moment(value, 'HH:mm');
                                const currentEndTime = moment(values.endTime, 'HH:mm');
                                if (newStartTime.isSameOrAfter(currentEndTime)) {
                                  setFieldValue('endTime', null);
                                }
                              }
                            }}
                            disabled={
                              modalStatus === 'View' ||
                              !values.startDate ||
                              isStartDateTimeExpired
                            }
                            errors={errors}
                            touched={touched}
                            inputType={modalStatus}
                            placeholder='Select Time'
                            required={(modalStatus === 'Add' || modalStatus === 'Edit') ? !values.startImmediately : true}
                            showOptional={(modalStatus === 'Add' || modalStatus === 'Edit') ? !values.startImmediately : false}
                            selectedDate={values.startDate}
                            minTime={getCurrentESTTime()}
                            compareDate={getTodayESTDate()}
                          />
                        </div>

                        {/* START IMMEDIATELY CHECKBOX - Below start date on new row (in Add and Edit modes) */}
                        {(modalStatus === 'Add' || modalStatus === 'Edit') && (
                          <div className='col-span-12 mb-4' id="startImmediatelyContainer">
                            <RadioButtonWrapper>
                              <input
                                type='checkbox'
                                id='startImmediately'
                                name='startImmediately'
                                checked={values.startImmediately}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setFieldValue('startImmediately', isChecked);
                                  if (isChecked) {
                                    setFieldValue('startTime', getCurrentESTTime());
                                    setFieldValue('startDate', getTodayESTDate());
                                  } else {
                                    // Clear start date and time when unchecking
                                    setFieldValue('startTime', null);
                                    setFieldValue('startDate', null);
                                  }
                                }}
                                disabled={isStartDateTimeExpired}
                              />
                              <LabelNormal htmlFor='startImmediately'>
                                Start Immediately
                              </LabelNormal>
                            </RadioButtonWrapper>
                          </div>
                        )}

                        {/* END DATE AND TIME - Same row */}
                        <div className='col-span-6' id="endDateContainer">
                          <CustomDatePicker
                            label='End Date'
                            name='endDate'
                            id='endDate'
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            values={values}
                            min={values?.startDate ? moment(values.startDate).toDate() : moment().toDate()}
                            onChange={() => {
                              // Clear end time when end date changes to force reselection
                              setFieldValue('endTime', null);
                            }}
                            disabled={values?.startDate === null || modalStatus === 'View'}
                            required
                          />
                        </div>

                        {/* END TIME PICKER - Material interface (visible in all modes) */}
                        <div className='col-span-6' id="endTimeContainer">
                          <MaterialTimePicker
                            label='End Time (EST)'
                            name='endTime'
                            id='endTime'
                            value={values.endTime}
                            onChange={(name, value) => {
                              setFieldValue(name, value);
                            }}
                            disabled={
                              modalStatus === 'View' ||
                              !values.endDate
                            }
                            errors={errors}
                            touched={touched}
                            inputType={modalStatus}
                            placeholder='Select Time'
                            required={true}
                            showOptional={false}
                            selectedDate={values.endDate}
                            minTime={values.startTime ? moment(values.startTime, 'HH:mm').add(1, 'minute').format('HH:mm') : null}
                            compareDate={values.startDate}
                          />
                        </div>

                        <FormSelect
                          containerClassName='col-span-6'
                          label='How would you to like to add Participants? '
                          name='blackoutEligibilityId'
                          id='blackoutEligibilityId'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          options={blackoutEligibilityOptions}
                          isLoading={isBlackoutEligibilityLoading}
                          values={values}
                          setFieldValue={setFieldValue}
                          disabled={modalStatus === 'View'}
                          required
                        />
                        {(blackoutEligibilityOptions
                          ?.find((item) => item.value === values?.blackoutEligibilityId)
                          ?.label.includes('Groups') ??
                          false) && (
                            <GroupOptions
                              errors={errors}
                              touched={touched}
                              values={values}
                              handleChange={handleChange}
                              modalStatus={modalStatus}
                              setFieldValue={setFieldValue}
                              setFieldTouched={setFieldTouched}
                              departmentOptions={departmentOptions}
                              employeeTypeOptions={employeeTypeOptions}
                              jobTitleOptions={jobTitleOptions}
                              handleRequest={handleRequest}
                            />
                          )}
                      </TradePreClearanceContainer>
                    </div>
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  id="tradingBlackoutConfigModalFooter"
                  modalStatus={modalStatus}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  loading={isLoading}
                  isEditShow={
                    !moment(
                      moment(initialBlackoutConfig?.endDate).format(DATE_FORMAT)
                    ).isSameOrBefore(moment().format(DATE_FORMAT), 'day') && true
                  }
                  valueChanged={_.isEqual(values, initialBlackoutConfig)}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default TradingBlackoutConfigurationModel;
